import React, { useState, useEffect, useRef } from "react"
import { Card, Button, Row, Col, Spin } from "antd"
import VideoService from "../services/VideoService"
import VimeoPlayer from "../components/VimeoPlayer"

type Lesson = {
    lessonId: number
    lessonName: string
    videoCount: number
}

type Grade = {
    gradeId: number
    gradeName: string
    videoCount: number
}

type VideoItem = {
    id: number
    title: string
    cover: string
    duration: number
    unitId?: number
    lessonId?: number
    subjectId?: number
}

type IVideoResponse = {
    id: number
    title: string
    vimeoId: string
    subjectId: number
    unitId: number
    lessonId: number
}

type VideoListProps = {
    tint?: string
}

// DERS İSMİ İLE VİDEO LİNKLERİNİN EŞLEŞMESİ
const lessonVideoMap: Record<string, string> = {
    Biyoloji: "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/biyoloji.mp4",
    "Biyoloji  Yeni Müfredat": "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/biyoloji.mp4",
    Coğrafya: "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/cografya.mp4",
    "Coğrafya Yeni Müfredat": "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/cografya.mp4",
    "Fen Bilgisi": "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/fen-bilgisi.mp4",
    Fizik: "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/fizik.mp4",
    "Fizik Yeni Müfredat": "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/fizik.mp4",
    Kimya: "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/kimya.mp4",
    Matematik: "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/matematik.mp4",
    "Matematik Yeni Müfredat": "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/matematik.mp4",
    "Sosyal Bilgiler": "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/sosyal-bilgiler.mp4",
    Tarih: "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/tarih.mp4",
    "T.C. İnkılap Tarihi ve Atatürkçülük": "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/inkilap-tarihi.mp4",
    Türkçe: "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/turkce.mp4",
    "Türk Dili ve Edebiyatı": "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/edebiyat.mp4",
    Edebiyat: "https://saha.fra1.cdn.digitaloceanspaces.com/website-videos/edebiyat.mp4",
}

export default function VideoList({ tint }: VideoListProps) {
    const [lessons, setLessons] = useState<Lesson[]>([])
    const [grades, setGrades] = useState<Grade[]>([])
    const [videos, setVideos] = useState<VideoItem[]>([])
    const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null)
    const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null)
    const [loadingLessons, setLoadingLessons] = useState<boolean>(true)
    const [loadingGrades, setLoadingGrades] = useState<boolean>(false)
    const [loadingVideos, setLoadingVideos] = useState<boolean>(false)
    const [hoveredLessonId, setHoveredLessonId] = useState<number | null>(null)
    const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({})

    // Video detay state
    const [video, setVideo] = useState<IVideoResponse | null>(null)
    const [previousVideo, setPreviousVideo] = useState<VideoItem | null>(null)
    const [nextVideo, setNextVideo] = useState<VideoItem | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [loaded, setLoaded] = useState(false)

    // Zaman formatı
    const formatDuration = (durationInSeconds: number): string => {
        const minutes = Math.floor(durationInSeconds / 60)
        const seconds = durationInSeconds % 60
        return `${minutes < 10 ? "0" + minutes : minutes}:${seconds < 10 ? "0" + seconds : seconds}`
    }

    // 1. Fetch all lessons and their video counts
    useEffect(() => {

        setTimeout(() => {
            setLoaded(true);
        }, 1000);

        if (!localStorage.getItem('accessToken')) {
            window.location.href = '/giris';
        }
        setLoadingLessons(true)
        fetch("https://api.sahadabasari.com/api/v1/website/lesson/video/count")
            .then(res => res.json())
            .then((data: Lesson[]) => {
                data = data.filter((l: Lesson) => l.lessonId !== 10 && l.videoCount > 0)
                setLessons(data)
            })
            .catch(err => console.error("Error fetching lessons:", err))
            .finally(() => setLoadingLessons(false))
    }, [])

    // 2. Fetch grades of selected lesson
    useEffect(() => {
        if (selectedLesson) {
            setLoadingGrades(true)
            fetch(`https://api.sahadabasari.com/api/v1/website/lesson/${selectedLesson.lessonId}/grade/video/count`)
                .then(res => res.json())
                .then((data: Grade[]) => setGrades(data))
                .catch(err => console.error("Error fetching grades:", err))
                .finally(() => setLoadingGrades(false))
        }
    }, [selectedLesson])

    // 3. Fetch videos for selected lesson and grade
    useEffect(() => {
        if (selectedLesson && selectedGrade) {
            setLoadingVideos(true)
            fetch(`https://api.sahadabasari.com/api/v1/website/video/free?lessonId=${selectedLesson.lessonId}&gradeId=${selectedGrade.gradeId}`)
                .then(res => res.json())
                .then((data: VideoItem[]) => setVideos(data))
                .catch(err => console.error("Error fetching videos:", err))
                .finally(() => setLoadingVideos(false))
        }
    }, [selectedLesson, selectedGrade])

    // Hover video play/pause kontrol
    useEffect(() => {
        lessons.forEach((lesson) => {
            const ref = videoRefs.current[lesson.lessonId]
            if (!ref) return
            if (hoveredLessonId === lesson.lessonId) {
                ref.play().catch(() => { })
            } else {
                ref.pause()
                ref.currentTime = 0
            }
        })
    }, [hoveredLessonId, lessons])

    // Kart stilleri
    const gridContainer = {
        display: "flex",
        flexWrap: "wrap" as "wrap",
        gap: "32px",
        justifyContent: "center",
        width: "100%",
    } as React.CSSProperties

    const cardStyle = {
        width: "340px",
        height: "340px",
        padding: "20px",
        borderRadius: "24px",
        cursor: "pointer",
        textAlign: "center" as const,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column" as const,
        color: "white",
        flex: "0 0 auto",
        background: "linear-gradient(135deg, rgb(114, 62, 230) 70%, rgb(93, 56, 170) 100%)",
        boxShadow: "rgba(60, 36, 102, 0.21) 0px 16px 48px 0px",
        transition: "transform 0.13s",
        position: "relative" as const,
        overflow: "hidden",
    } as React.CSSProperties

    const videoBgStyle = {
        position: "absolute" as const,
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover" as const,
        zIndex: 0,
        opacity: 0.55,
        pointerEvents: "none" as const,
        transition: "opacity 0.13s",
        background: "#000",
    } as React.CSSProperties

    const contentStyle = {
        position: "relative" as const,
        zIndex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
    } as React.CSSProperties

    const gradeCardStyle = {
        ...cardStyle,
        background: "linear-gradient(135deg, #28293d 70%, #28293d 100%)",
        color: "#fff",
    } as React.CSSProperties

    const videoCardStyle = {
        minWidth: "340px",
        maxWidth: "340px",
        minHeight: "230px",
        padding: "18px",
        borderRadius: "16px",
        color: "white",
        textAlign: "center" as const,
        cursor: "pointer",
        border: "1px solid #222",
        boxShadow: "rgba(60, 36, 102, 0.12) 0px 8px 28px 0px",
        flex: "0 0 auto",
        background: "#181823",
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        transition: "transform 0.13s",
    } as React.CSSProperties

    // Video oynatıcı SPA
    const isDetailOpen = !!video

    // Video izleme history fonksiyonu örneği (implementasyonu sana bağlı)
    const videHistoryPost = (
        videoId: number,
        subjectId: number,
        courseId: number | undefined,
        duration: number
    ) => {
        // ...
    }

    // Detay aç
    const handleVideoClick = (videoItem: VideoItem, index: number) => {
        setLoadingDetail(true)
        VideoService.get(videoItem.id)
            .then((response: any) => {
                const data: IVideoResponse = response.data
                setVideo(data)
                setLoadingDetail(false)
                setPreviousVideo(videos[index - 1] ?? null)
                setNextVideo(videos[index + 1] ?? null)
            })
            .catch((e: Error) => {
                setLoadingDetail(false)
            })
    }

    const handleBack = () => {
        setVideo(null)
        setPreviousVideo(null)
        setNextVideo(null)
    }

    // Responsive grid için ek stil
    const responsiveGridStyle = `
        @media (max-width: 800px) {
            .lesson-card { width: 95vw !important; height: 200px !important; }
            .video-card { min-width: 95vw !important; max-width: 95vw !important; }
        }
        @media (max-width: 1200px) {
            .lesson-card { width: 300px !important; height: 220px !important; }
        }
    `

    return (
        loaded ? <div
            style={{
                width: "100%",
                backgroundColor: tint || "#f8f9fc",
                padding: 25,
                fontFamily: "Montserrat, sans-serif",
                minHeight: "100vh",
                boxSizing: "border-box",
            }}
        >
            <style>{responsiveGridStyle}</style>
            {((selectedLesson || selectedGrade) && !isDetailOpen) && (
                <div style={{
                    marginBottom: 24,
                    display: "flex",
                    justifyContent: "start"
                }}>
                    <button
                        onClick={() => {
                            if (isDetailOpen) handleBack()
                            else if (selectedGrade) setSelectedGrade(null)
                            else if (selectedLesson) setSelectedLesson(null)
                        }}
                        style={{
                            padding: "10px 22px",
                            fontSize: 16,
                            cursor: "pointer",
                            border: "none",
                            color: "#fff",
                            borderRadius: 8,
                            backgroundColor: "#000",
                        }}
                    >
                        Geri
                    </button>
                </div>
            )}

            {/* DERS SEÇİMİ */}
            {!selectedLesson && (
                loadingLessons ? (
                    <div style={{
                        textAlign: "center",
                        color: "#4b3474",
                        fontWeight: 500,
                        fontSize: 20,
                        opacity: 0.7,
                        padding: "40px 0",
                    }}>Yükleniyor...</div>
                ) : (
                    <div style={gridContainer}>
                        {lessons.map((lesson) => {
                            const videoUrl = lessonVideoMap[lesson.lessonName?.trim() as string] || null
                            return (
                                <div
                                    key={lesson.lessonId}
                                    style={cardStyle}
                                    className="lesson-card"
                                    onClick={() => {
                                        setSelectedLesson(lesson)
                                        setGrades([])
                                        setSelectedGrade(null)
                                    }}
                                    onMouseEnter={() => setHoveredLessonId(lesson.lessonId)}
                                    onMouseLeave={() => setHoveredLessonId(null)}
                                >
                                    {videoUrl && (
                                        <video
                                            ref={el => (videoRefs.current[lesson.lessonId] = el)}
                                            style={videoBgStyle}
                                            src={videoUrl}
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                        />
                                    )}
                                    <div style={contentStyle}>
                                        <h2 style={{ marginBottom: 5 }}>{lesson.lessonName}</h2>
                                        <p>{lesson.videoCount} Video</p>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )
            )}

            {/* SINIF (GRADE) SEÇİMİ */}
            {selectedLesson && !selectedGrade && (
                loadingGrades ? (
                    <div style={{
                        textAlign: "center",
                        color: "#4b3474",
                        fontWeight: 500,
                        fontSize: 20,
                        opacity: 0.7,
                        padding: "40px 0",
                    }}>Yükleniyor...</div>
                ) : (
                    <div style={gridContainer}>
                        {grades.map((grade) => (
                            <div
                                key={grade.gradeId}
                                style={gradeCardStyle}
                                className="lesson-card"
                                onClick={() => {
                                    setSelectedGrade(grade)
                                    setVideos([])
                                }}
                            >
                                <h2 style={{ marginBottom: 5 }}>{grade.gradeName}</h2>
                                <p>{grade.videoCount} Video</p>
                            </div>
                        ))}
                    </div>
                )
            )}

            {/* VİDEO KARTLARI */}
            {selectedLesson && selectedGrade && !isDetailOpen && (
                loadingVideos ? (
                    <div style={{
                        textAlign: "center",
                        color: "#4b3474",
                        fontWeight: 500,
                        fontSize: 20,
                        opacity: 0.7,
                        padding: "40px 0",
                    }}>Yükleniyor...</div>
                ) : (
                    <div style={gridContainer}>
                        {videos.length > 0 ? (
                            videos.map((video, idx) => (
                                <div
                                    key={video.id}
                                    style={{ ...videoCardStyle }}
                                    className="video-card"
                                    onClick={() => handleVideoClick(video, idx)}
                                >
                                    <img
                                        src={video.cover}
                                        alt={video.title}
                                        style={{
                                            width: "100%",
                                            height: "180px",
                                            borderRadius: "8px",
                                            objectFit: "cover",
                                            marginBottom: 10,
                                        }}
                                    />
                                    <h3 style={{
                                        color: "#fff",
                                        marginTop: 2,
                                        fontWeight: 600,
                                        fontSize: 18,
                                    }}>{video.title}</h3>
                                    <p style={{
                                        color: "#bbb",
                                        marginTop: 4,
                                        fontSize: 14,
                                    }}>{formatDuration(video.duration)}</p>
                                </div>
                            ))
                        ) : (
                            <p>Bu seçime ait video yok.</p>
                        )}
                    </div>
                )
            )}

            {/* VİDEO DETAY - SPA CARD */}
            {isDetailOpen && (
                <Card
                    loading={loadingDetail}
                    title={video?.title}
                    style={{ margin: "0" }}
                    bodyStyle={{ paddingTop: 0 }}
                    extra={
                        <Row gutter={[16, 16]} justify={"space-between"} align={"middle"}>
                            <Col>
                                {previousVideo && (
                                    <Button
                                        disabled={loadingDetail}
                                        type="primary"
                                        onClick={() => {
                                            if (previousVideo) {
                                                const idx = videos.findIndex(v => v.id === previousVideo.id)
                                                handleVideoClick(previousVideo, idx)
                                            }
                                        }}
                                    >
                                        Önceki Video
                                    </Button>
                                )}
                            </Col>
                            <Col>
                                {nextVideo && (
                                    <Button
                                        disabled={loadingDetail}
                                        type="primary"
                                        onClick={() => {
                                            if (nextVideo) {
                                                const idx = videos.findIndex(v => v.id === nextVideo.id)
                                                handleVideoClick(nextVideo, idx)
                                            }
                                        }}
                                    >
                                        Sonraki Video
                                    </Button>
                                )}
                            </Col>
                            <Col>
                                <Button
                                    onClick={handleBack}
                                >
                                    Listeye Dön
                                </Button>
                            </Col>
                        </Row>
                    }
                >
                    {video?.vimeoId && (
                        <Row>
                            <Col span={24}>
                                <VimeoPlayer
                                    videoId={video.vimeoId}
                                    onTimeUpdate={(duration: any) => {
                                        videHistoryPost(
                                            video.id,
                                            video.subjectId,
                                            selectedGrade?.gradeId,
                                            duration
                                        )
                                    }}
                                />
                            </Col>
                        </Row>
                    )}
                </Card>
            )}
        </div> : <></>

    )
}
